import { addToChainAfter, executeAwaitableHandlerChain, replaceInChain } from "@insite/client-framework/HandlerCreator";
import { getProductCollectionV2 } from "@insite/client-framework/Services/ProductServiceV2";
import loadVariantChildren from "@insite/client-framework/Store/Data/Products/Handlers/LoadVariantChildren";
import { getVariantChildrenDataView } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import {
    chain,
    LoadProductIfNeeded,
    RequestVariantChildrenFromApi,
    SetupVariantSelection,
} from "@insite/client-framework/Store/Pages/ProductDetails/Handlers/DisplayProduct";
import { DISCONTINUED_BUT_VISIBLE, IS_CUSTOM_PRODUCT, PARENT_PRODUCT_URL } from "@thkStore/Constants/ProductPropertiesKeys";

type DisplayProductHandlerProps = Parameters<typeof RequestVariantChildrenFromApi>[0];

const CustomRequestVariantChildrenFromApi = async (props: DisplayProductHandlerProps) => {
    if (!props.product?.isVariantParent) {
        return;
    }

    props.variantChildren = getVariantChildrenDataView(props.getState(), props.product.id).value;
    if (!props.variantChildren) {
        props.variantChildren = await executeAwaitableHandlerChain(
            loadVariantChildren,
            { productId: props.product.id },
            props,
        );
    }
};

const RedirectDiscontinuedVariantToParent = (props: DisplayProductHandlerProps) => {
    if (typeof window === "undefined") {
        return;
    }

    if (props.product?.isVariantParent) {
        return;
    }

    const parentProductUrl = props.product?.properties?.[PARENT_PRODUCT_URL];
    const isDiscontinuedButVisible = props.product?.properties?.[DISCONTINUED_BUT_VISIBLE] === "true";
    const isCustomProduct = props.product?.properties?.[IS_CUSTOM_PRODUCT] === "true";
    if ((isDiscontinuedButVisible || isCustomProduct) && parentProductUrl) {
        window.location.replace(parentProductUrl);
        return false;
    }
};

/**
 * Resolves a single variant child by its product/ERP number.
 *
 * The variant parent's children are loaded client-side with a capped page size
 * (the server clamps page size to the `ProductsApiPageSizeMaximum` setting), so a
 * parent with thousands of children (e.g. KR has 1,520) never has every child in
 * the loaded list. Scanning that truncated list fails to find children in the tail.
 *
 * This makes one targeted API call filtered by ERP number, which scales regardless
 * of how many children the parent has.
 */
const resolveChildByProductNumber = async (styledOption: string) => {
    const code = styledOption.toLowerCase();

    // 1. Exact match on product number. `productNumbers` is an exact-ERP filter and the backend
    //    bumps the page size for product-number queries, so an existing child can never be dropped
    //    by a small page limit (the bug behind "works for some, not others").
    let result = await getProductCollectionV2({ productNumbers: [styledOption], pageSize: 50 });
    let match = result.products?.find((p: any) => p.productNumber?.toLowerCase() === code);

    // 2. Fallback to extendedNames (alternate / customer names) only if the exact ERP filter missed.
    if (!match?.urlSegment) {
        result = await getProductCollectionV2({ extendedNames: [styledOption], pageSize: 50 });
        match = result.products?.find((p: any) => p.productNumber?.toLowerCase() === code);
    }

    return match;
};

/**
 * Runs after SetupVariantSelection in the DisplayProduct chain.
 *
 * When the PDP is reached via a `?option=` URL (e.g. from wish lists, search,
 * cart links, or external bookmarks), this handler finds the matching variant
 * child product and rewrites the browser URL from the query-string form
 *   `/Product/parent-slug?option=VARIANT-NUM`
 * to the slug-based form
 *   `/Product/variant-slug`
 *
 * The child is taken from the already-loaded variant children when present;
 * for parents whose child count exceeds the API page-size cap, it falls back to
 * a targeted ERP lookup (see resolveChildByProductNumber).
 */
const RewriteOptionUrlToSlug = async (props: any) => {
    // Only run in browser (skip during SSR)
    if (typeof window === "undefined") {
        return;
    }

    const styledOption = props.parameter?.styledOption;
    if (!styledOption) {
        return;
    }

    // 1. Fast path: try the already-loaded variant children (same logic as SetupVariantSelection)
    let matchedChild = props.variantChildren?.find(
        (child: any) => child.productNumber?.toLowerCase() === styledOption.toLowerCase(),
    );

    // 2. Fallback: child not in the (page-size-capped) loaded list — resolve it directly by ERP number.
    if (!matchedChild?.urlSegment) {
        try {
            matchedChild = await resolveChildByProductNumber(styledOption);
        } catch {
            // Network/API failure — leave the user on the current ?option= URL rather than mis-redirecting.
            return;
        }
    }

    if (!matchedChild?.urlSegment) {
        return;
    }

    // Discontinued/custom children should resolve to the parent, not their own slug.
    // Note: on the collection endpoint only `discontinuedButVisible` is populated; a custom
    // product reaching the child slug below is still caught by RedirectDiscontinuedVariantToParent
    // (which runs on the child PDP using the single-product properties).
    if (
        matchedChild.properties?.[DISCONTINUED_BUT_VISIBLE] === "true" ||
        matchedChild.properties?.[IS_CUSTOM_PRODUCT] === "true"
    ) {
        window.location.replace(`${window.location.origin}${window.location.pathname}`);
        return false;
    }

    // Build the new slug-based URL by replacing the last path segment
    const currentUrl = new URL(window.location.href);
    const pathSegments = currentUrl.pathname.split("/");
    pathSegments[pathSegments.length - 1] = matchedChild.urlSegment;
    const newPath = pathSegments.join("/");

    // Full-page redirect to the slug URL.
    // Uses the absolute URL to ensure the browser does a true navigation
    // (not an SPA client-side route change). Replaces the history entry
    // so the back button skips the ?option= URL.
    if (newPath !== currentUrl.pathname) {
        window.location.replace(`${window.location.origin}${newPath}`);
        return false; // Stop the handler chain — page is redirecting
    }
};

addToChainAfter(chain, LoadProductIfNeeded, RedirectDiscontinuedVariantToParent);

replaceInChain(chain, RequestVariantChildrenFromApi, async (props: DisplayProductHandlerProps) => {
    await CustomRequestVariantChildrenFromApi(props);
});

addToChainAfter(chain, SetupVariantSelection, RewriteOptionUrlToSlug);